import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import CustomModal from "../../components/modal/CustomModal";
import { MAX_UPLOAD_FILE_SIZE_IMPORT } from "../../constants/form";
import { DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";
import alertService from "../../components/alert/alertService";
import FileUtils from "../../utils/fileUtils";

import { useAuth } from "../../contexts/UserContext";
import { ImportFileContext } from "../../contexts/ImportFileContext";

import { ImportFileErrorTable } from "./ImportFileErrorTable";
import i18n, { common, input } from "../../i18n";
import { UploadFileWithOutPreview } from "../../components/input/UploadFileWithOutPreview";
import {
  ActivitiesRef,
  DataError,
  ImportActivitiesHistory,
  UploadItem,
  ValidateFileResponse,
} from "./ImportFilePopup.type";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { AppDataGrid } from "../../components/datagrid/AppDataGrid";
import { ImportDataStepThree } from "./ImportFlow/ImportDataStepThree";
import ImportFileService from "../../service/import-file.service";
import { ApiError } from "../../api/ApiError";
import InfoPopper from "../../components/popper/InfoPopper";
import axios from "axios";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import dayjs, { Dayjs } from "dayjs";
import NumberFormatterUtils from "../../utils/numberFormatterUtils";
import { tableStyle } from "../emission/_components/data-table/EmissionDataTableStyle";
import { OrganizationBreadcrumbs } from "../emission/_components/OrganizationBreadcrumbs";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 16,
    border: "0",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    backgroundColor: "var(--gray3)",
  },
}));

export const ImportFilePopup = () => {
  const { t } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const context = useContext(OrganizationContext);

  const { organization } = useAuth();

  const { downloadData } = ImportFileContext();

  const [open, setOpen] = useState<boolean>(false);

  const onClose = async () => {
    let confirm = false;

    if (activeStep >= 3 && importResult.every((i) => i.attachmentRef)) {
      confirm = true;
    } else {
      confirm = await alertService.confirm(
        activeStep < 3 ? t("Close") : t("Successfully"),
        activeStep < 3 ? (
          t("Are-you-sure-you-want-to-close-this-page")
        ) : (
          <Trans
            t={t}
            i18nKey="Return-To-Upload-Later"
            components={{
              span: <span />,
            }}
          />
        ),
        activeStep < 3 ? undefined : t("Save-And-Upload-Later"),
      );
    }

    // const confirm = importResult.every((i) => i.attachmentRef)
    //   ? true
    //   : await alertService.confirm(
    //       activeStep < 3 ? t("Close") : t("Successfully"),
    //       activeStep < 3 ? (
    //         t("Are-you-sure-you-want-to-close-this-page")
    //       ) : (
    //         <Trans
    //           t={t}
    //           i18nKey="Return-To-Upload-Later"
    //           components={{
    //             span: <span />,
    //           }}
    //         />
    //       ),
    //       activeStep < 3 ? undefined : t("Save-And-Upload-Later")
    //     );

    if (confirm) {
      // a refused file is saved as a Failed history row at any step; reloading earlier would hide its alert
      downloadData();

      if (activeStep < 3) cancelImportData();

      setActiveStep(0);
      setFileName("");
      setValidateFileResponse(null);
      setDataError([]);
      setUploadFiles([]);
      setFiles([]);
      setFileErrors([]);
      setOpen(false);
      setImportResult([]);
    }
  };

  const steps = [
    t("Upload-attachment"),
    t("Review-File"),
    t("Upload-Evidence"),
    t("Review-Evidence"),
  ];

  const [fileName, setFileName] = useState<string>("");

  const [validateFileResponse, setValidateFileResponse] =
    useState<ValidateFileResponse | null>(null);

  const [dataError, setDataError] = useState<DataError[]>([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropRejected(fileRejections, event) {
      if (fileRejections[0].errors[0].code === "file-too-large") {
        return alertService.error(
          t("maximum-file-size", {
            size: (MAX_UPLOAD_FILE_SIZE_IMPORT / 1e6).toFixed(0),
          }),
        );
      } else {
        return alertService.error(t("Invalid-File-Type"));
      }
    },
    async onDrop(acceptedFiles, fileRejections, event) {
      let res: string;
      try {
        res = (await FileUtils.getBase64(acceptedFiles[0])) as string;
      } catch (error) {
        if (error instanceof ApiError) return;
        alertService.error(t("Invalid-File-Type"));
        return;
      }

      let result: ValidateFileResponse | null = null;
      try {
        result = await ImportFileService.validateFile(
          acceptedFiles[0].name,
          res,
          validateFileResponse?.importActivitiesRef ?? undefined,
        );
      } catch {
        // alert already shown by handleApiError inside validateFile
        return;
      }

      if (result) {
        setValidateFileResponse(result);

        if (result.dataErrors) {
          downloadData();
          setDataError([...result.dataErrors]);
        } else {
          setFileName(acceptedFiles[0].name ?? "");
          setDataError([]);
          setActiveStep(1);
        }
      }
    },
    maxFiles: 1,
    maxSize: MAX_UPLOAD_FILE_SIZE_IMPORT,
    multiple: false,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    },
  });

  const [activeStep, setActiveStep] = useState<number>(0);
  const [isImporting, setIsImporting] = useState<boolean>(false);

  const importFile = async () => {
    if (isImporting) return;
    setIsImporting(true);
    const fileIds = new Set(files.map((f) => f.file.name));

    const missingFromFiles: ImportActivitiesHistory[] = uploadFiles.filter(
      (u) => !fileIds.has(u.evidenceFileName),
    );

    if (context?.organization && validateFileResponse && organization) {
      alertService.showLoading();
      try {
        const result = await ImportFileService.importFile(
          organization.id,
          context.organization._id,
          validateFileResponse,
        );

        if (result) {
          alertService.mixin(t("Successfully"));

          setActiveStep(activeStep + 1);

          const activitiesRef: ActivitiesRef[] =
            result.importActivitiesHistory[0]?.activitiesRef ?? [];

          missingFromFiles.forEach((e, index) => {
            const fileName: string = e.evidenceFileName;

            const error: FileRejection | undefined = fileErrors.find(
              (f) => f.file.name === fileName,
            );

            activitiesRef[index].error = error;
          });

          setImportResult(activitiesRef);
          if ((missingFromFiles.length = 0)) onClose();
        }
      } catch {
        // error alert already shown by service
      } finally {
        alertService.closeLoading();
        setIsImporting(false);
      }
    } else {
      setIsImporting(false);
    }
  };

  const [uploadFiles, setUploadFiles] = useState<ImportActivitiesHistory[]>([]);

  const [files, setFiles] = useState<UploadItem[]>([]);

  const [fileErrors, setFileErrors] = useState<FileRejection[]>([]);

  const [allSuccessful, setAllSuccessful] = useState<boolean>(true);

  // upload evidence file
  const createUploadList = async (acceptedFiles: File[]) => {
    const map = new Map<string, UploadItem>();

    files.forEach((item) => {
      map.set(item.file.name, item);
    });

    for (const a of acceptedFiles) {
      const existing = map.get(a.name);

      if (!existing || existing.status !== "successfully") {
        const newItem: UploadItem = {
          file: a,
          progress: 0,
          status: "progress",
        };
        map.set(a.name, newItem);
      }
    }

    return Array.from(map.values());
  };
  const dropzoneOptions: DropzoneOptions = {
    async onDrop(acceptedFiles, fileRejections, event) {
      const matches: { file: File; activity: ImportActivitiesHistory }[] = [];

      uploadFiles.forEach((activity) => {
        const file = acceptedFiles.find(
          (f) => f.name === activity.evidenceFileName,
        );

        const isAlreadyUploaded = files.find(
          (f) =>
            f.file.name === activity.evidenceFileName &&
            f.status === "successfully",
        );

        if (file && !isAlreadyUploaded) {
          matches.push({
            file: file,
            activity: activity,
          });
        }
      });

      const uniqueFilesToProcess = Array.from(
        new Set(matches.map((m) => m.file)),
      );
      const uploadList: UploadItem[] = await createUploadList(
        uniqueFilesToProcess,
      );

      const unMatchFile: File[] = acceptedFiles.filter(
        (a) => !uploadFiles.find((u) => u.evidenceFileName === a.name),
      );

      for (const match of matches) {
        const { file, activity } = match;

        const uiItemIndex = uploadList.findIndex(
          (u) => u.file.name === file.name,
        );

        if (uiItemIndex !== -1) {
          uploadList[uiItemIndex].status = "progress";
          setFiles([...uploadList]);

          try {
            await axios.put(activity.evidenceS3UrlUpload, file, {
              headers: {
                "Content-Type": file.type,
              },
              onUploadProgress: (progressEvent) => {
                const percent = Math.round(
                  (progressEvent.loaded * 100) / (progressEvent.total || 1),
                );
                // อัปเดต % เฉพาะตัวนี้
                uploadList[uiItemIndex].progress = percent;
                setFiles([...uploadList]);
              },
            });

            uploadList[uiItemIndex].status = "successfully";
          } catch (error) {
            console.error(
              `Failed to upload ${activity.evidenceFileName}`,
              error,
            );
            uploadList[uiItemIndex].status = "failed";
          }

          setFiles([...uploadList]);
        }
      }

      if (fileRejections) {
        const temp = [...fileErrors, ...fileRejections].filter(
          (file, index, self) =>
            index === self.findIndex((f) => f.file.name === file.file.name),
        );

        setFileErrors([
          ...temp.filter(
            (t) => !uploadList.find((f) => f.file.name === t.file.name),
          ),
        ]);
      }

      if (unMatchFile.length > 0) {
        alertService.confirm(
          t("File-Mismatch-Headline"),
          <Box display={"flex"} flexDirection={"column"} gap={"8px"}>
            <List>
              {unMatchFile.map((f) => (
                <ListItem key={f.name}>
                  <ListItemIcon>
                    <FolderOffIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary={f.name} />
                </ListItem>
              ))}
            </List>
          </Box>,
          t("Close"),
        );
      }
    },
    maxSize: MAX_UPLOAD_FILE_SIZE_IMPORT,
    accept: {
      "image/*": [],
      "application/pdf": [],
      "text/csv": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "application/vnd.ms-excel": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
    },
  };

  const columns: GridColDef<ImportActivitiesHistory>[] = [
    {
      field: "id",
      headerName: t("ID"),
      disableColumnMenu: true,
      type: "string",
      headerAlign: "left",
      align: "left",
      valueFormatter: (value, row) => `EM-${row.id}`,
    },
    {
      field: "startDate",
      headerName: t("Date"),
      width: 100,
      disableColumnMenu: true,
      headerAlign: "left",
      type: "date",
      valueFormatter: (value: string, row) => {
        const start: Dayjs = dayjs(value.substring(0, 10));

        const end: Dayjs = dayjs(row.endDate.substring(0, 10));

        if (start.isSame(end, "month")) {
          return start.locale(i18n.language).format("MMM YYYY");
        } else {
          return `${start.locale(i18n.language).format("MMM YYYY")} -\n${end
            .locale(i18n.language)
            .format("MMM YYYY")}`;
        }
      },
      renderCell: (params) => {
        return (
          <Typography variant="text1" sx={{ whiteSpace: "pre-line" }}>
            {params.formattedValue}
          </Typography>
        );
      },
    },
    {
      field: "siteName",
      headerName: t("Site"),
      headerAlign: "left",
      disableColumnMenu: true,
      valueFormatter: (value, row) => row.siteName,
      minWidth: 180,
      flex: 1,
    },

    {
      field: "assetName",
      headerName: t("Asset"),
      headerAlign: "left",
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      valueFormatter: (value, row) => row.assetName,
      minWidth: 180,
      flex: 1,
    },
    {
      field: "activityAttributeEn",
      headerName: t("Emission-Source"),
      headerAlign: "left",
      disableColumnMenu: true,
      minWidth: 180,
      valueFormatter: (value, row) => {
        return i18n.language.toLowerCase().includes("en")
          ? row.activityAttributeEn
          : row.activityAttributeTh;
      },
      flex: 1,
    },
    {
      field: "amount",
      headerName: t("Amount"),
      disableColumnMenu: true,
      type: "number",
      minWidth: 160,
      valueFormatter: (v) => NumberFormatterUtils.numberFormat(v),
    },
    {
      field: "evidenceFileName",
      headerName: t("File-Name"),
      sortable: false,
      headerAlign: "left",
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      minWidth: 180,
      flex: 1,
      renderCell: (params: GridRenderCellParams<ImportActivitiesHistory>) => {
        const file = files.find(
          (f) => f.file.name === params.row.evidenceFileName,
        );

        return (
          <>
            {file ? (
              <Link
                href={
                  file && file.status === "successfully"
                    ? params.row.evidenceS3Url
                    : undefined
                }
                underline={file ? "hover" : "none"}
                target="_blank"
                color={"var(--dark)"}
                sx={{
                  cursor: "pointer",
                }}
              >
                {params.value}
              </Link>
            ) : (
              params.value
            )}
          </>
        );
      },
    },
    {
      field: "progress",
      headerName: t("Progress"),
      width: 200,
      type: "number",
      headerAlign: "left",
      valueGetter: (params, row) => {
        const file = files.find((f) => f.file.name === row.evidenceFileName);
        return file?.progress ?? 0;
      },
      renderCell: (params: GridRenderCellParams<ImportActivitiesHistory>) => {
        const file = files.find(
          (f) => f.file.name === params.row.evidenceFileName,
        );

        const fileError = fileErrors.find(
          (f) => f.file.name === params.row.evidenceFileName,
        );

        return (
          <Box width="100%">
            <Box textAlign={"right"}>
              <Typography variant="text2">{file?.progress ?? 0}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={file?.progress ?? 0}
              color={
                file === undefined && fileError === undefined
                  ? "secondary"
                  : file && file.status === "successfully"
                  ? "primary"
                  : fileError || (file && file.status === "failed")
                  ? "error"
                  : "info"
              }
              sx={{
                width: "100%",
                height: 8,
                borderRadius: 5,
              }}
            />
            <Box textAlign={"left"}>
              <Typography
                variant="text2"
                color={
                  file === undefined && fileError === undefined
                    ? "var(--dark)"
                    : file && file.status === "successfully"
                    ? "var(--green)"
                    : "var(--red)"
                }
                textAlign={"left"}
              >
                {file === undefined &&
                  fileError === undefined &&
                  t("Not-Uploaded")}

                {file?.status === "successfully" && t("Successfully")}

                {file?.status === "failed" && (
                  <Typography
                    variant="text2"
                    sx={{
                      marginBottom: "8px",
                      borderBlockStart: "1px",
                    }}
                    textAlign={"left"}
                  >
                    {t("Action-Failed")}

                    <InfoPopper
                      text={t("Processing-Failed")}
                      infoIconColor={"error"}
                    />
                  </Typography>
                )}

                {fileError && fileError.errors[0].code === "file-too-large" && (
                  <Typography
                    variant="text2"
                    sx={{
                      marginBottom: "8px",
                      borderBlockStart: "1px",
                    }}
                    textAlign={"left"}
                  >
                    {t("Validation-Failed")}

                    <InfoPopper
                      text={t("maximum-file-size", {
                        size: (MAX_UPLOAD_FILE_SIZE_IMPORT / 1e6).toFixed(0),
                      })}
                      infoIconColor={"error"}
                    />
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  const cancelImportData = () => {
    const fileIds = new Set(files.map((f) => f.file.name));

    const deleteItems: ImportActivitiesHistory[] = uploadFiles.filter((u) =>
      fileIds.has(u.evidenceFileName),
    );

    const deleteUrls = Array.from(
      new Set(deleteItems.map((f) => f.evidenceS3UrlDelete)).values(),
    );

    for (const d of deleteUrls) {
      try {
        axios.delete(d);
      } catch (error) {}
    }
  };

  const [importResult, setImportResult] = useState<ActivitiesRef[]>([]);

  useEffect(() => {
    setUploadFiles([...uploadFiles]);
    setAllSuccessful(files.every((file) => file.status !== "progress"));
  }, [files, fileErrors]);

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<img src="/img/document-upload.svg" alt="" />}
        onClick={() => setOpen(true)}
      >
        {t("Upload-File")}
      </Button>

      <CustomModal
        header={
          <>
            <Typography variant="h1">{t("Upload-File")}</Typography>
            <OrganizationBreadcrumbs
              organization={context?.organization?.name ?? ""}
              location={""}
              subOrganizations={""}
            />
          </>
        }
        body={
          <Box
            sx={{
              width: "100%",
            }}
            paddingTop={"44px"}
            // paddingBottom={"24px"}
            display={"flex"}
            flexDirection={"column"}
            gap={"20px"}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                marginBottom: "40px",
                width: "100%",
              }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    color="secondary"
                    sx={{
                      ".MuiStepLabel-label": {
                        fontSize: "16px !important",
                        color:
                          activeStep > index ? "var(--green)" : "var(--dark)",
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 ? (
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed grey",
                  borderRadius: "8px",
                  padding: "16px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragActive ? "#f0f0f0" : "#fafafa",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                }}
              >
                <input {...getInputProps()} />
                <Box color={"var(--gray4)"}>
                  <Typography variant="body1">
                    <Trans
                      t={t}
                      i18nKey="drag-drop-or-browse"
                      components={{
                        span: <span style={{ color: "var(--green)" }} />,
                      }}
                    />
                  </Typography>
                  <Typography variant="body1">
                    <Trans
                      t={t}
                      i18nKey="maximum-file-size"
                      values={{
                        size: (MAX_UPLOAD_FILE_SIZE_IMPORT / 1e6).toFixed(0),
                      }}
                    />
                  </Typography>
                </Box>
              </Box>
            ) : activeStep === 1 ? (
              <>
                <TableContainer>
                  {/* <Typography
                    variant="h3"
                    color={"var(--dark)"}
                    sx={{
                      marginBottom: "8px",
                      borderBlockStart: "1px",
                    }}
                  >
                    {`${t("Files")}`}

                    <InfoPopper text={t("Files")} />
                  </Typography> */}
                  <Table
                    sx={{ width: "100%", marginY: "20px" }}
                    aria-label="simple table"
                  >
                    <TableHead
                      sx={{
                        fontSize: "16px",
                      }}
                    >
                      <TableRow>
                        <StyledTableCell>{t("File-Name")}</StyledTableCell>
                        <StyledTableCell>{t("Success-Row")}</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={fileName}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <StyledTableCell component="th" scope="row">
                          {fileName}
                        </StyledTableCell>
                        <StyledTableCell>
                          {validateFileResponse?.numImportActivitiesHistory ??
                            0}
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  variant="contained"
                  color="secondary"
                  type="button"
                  fullWidth
                  onClick={() => {
                    if (validateFileResponse) {
                      setUploadFiles(
                        validateFileResponse?.importActivitiesHistory,
                      );
                    }
                    setActiveStep(2);
                  }}
                >
                  {t("Next")}
                </Button>
              </>
            ) : activeStep === 2 ? (
              <>
                <UploadFileWithOutPreview dropZoneOptions={dropzoneOptions} />
                <Box marginBottom={3}>
                  <Box height={"320px"}>
                    <AppDataGrid
                      rows={uploadFiles}
                      columns={columns}
                      rowHeight={60}
                      getRowId={(row: ImportActivitiesHistory) => row.id}
                      sx={tableStyle()}
                    />
                  </Box>
                </Box>
                <Box
                  position="sticky"
                  bottom={0}
                  bgcolor="white"
                  pt={1}
                  zIndex={1}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    type="button"
                    fullWidth
                    onClick={importFile}
                    disabled={!allSuccessful || isImporting}
                  >
                    {allSuccessful ? (
                      t("Import")
                    ) : (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        {t("Uploading")}
                      </>
                    )}
                  </Button>
                </Box>
              </>
            ) : activeStep === 3 ? (
              <>
                <ImportDataStepThree
                  onClose={onClose}
                  activitiesRef={importResult}
                  fileErrors={fileErrors}
                  setImportResult={setImportResult}
                />
              </>
            ) : undefined}
            <ImportFileErrorTable rows={dataError} />
          </Box>
        }
        open={open}
        onClose={onClose}
      />
    </>
  );
};
